/**
 * EST3 client BOQ import parser — KEEP_SEPARATE from Manufacturing `boqLineImportParser`.
 */
import {
  type BoqImportColumnKey,
  type BoqImportMappingPayload,
  type Est3BoqImportPreset,
  type ResolvedBoqImportColumns,
  applyExcelBoqHierarchy,
  columnsPayloadFromResolved,
  resolveBoqImportColumns,
} from './est3BoqImportMapping';

export type { Est3BoqImportPreset, BoqImportMappingPayload, ResolvedBoqImportColumns, BoqImportColumnKey };
export {
  BOQ_IMPORT_PRESET_HINTS,
  BOQ_IMPORT_PRESET_LABELS,
  BOQ_IMPORT_TARGET_FIELDS,
  detectBoqImportPreset,
  columnsPayloadFromResolved,
  resolveBoqImportColumns,
  resolveParentItemNumber,
  applyExcelBoqHierarchy,
} from './est3BoqImportMapping';

export interface Est3BoqImportPreviewRow {
  key: number;
  row: number;
  sheet?: string;
  itemNumber?: string;
  description: string;
  parentItemNumber?: string;
  quantity: number;
  uom?: string;
  unitRate: number;
  notes?: string;
  amountText?: string;
  status: 'valid' | 'error' | 'warning';
  errorMessage?: string;
  warningMessage?: string;
  detectedPreset?: string;
}

export interface Est3BoqImportWorkbookPreview {
  sheetName: string;
  sheetNames: string[];
  headerRowIndex: number;
  headers: string[];
  sampleRows: unknown[][];
  aoa: unknown[][];
  sheets: Array<{ name: string; aoa: unknown[][] }>;
}

const AMOUNT_EPSILON = 0.01;

function cellStr(row: unknown[], idx: number): string {
  if (idx < 0) return '';
  const v = row[idx];
  return v != null ? String(v).trim() : '';
}

function cellNum(row: unknown[], idx: number): number {
  const s = cellStr(row, idx);
  if (!s) return NaN;
  return Number(s.replace(/,/g, ''));
}

export function applyParentsFromExcelItemNumbers(
  rows: Est3BoqImportPreviewRow[],
  derive = true
): Est3BoqImportPreviewRow[] {
  if (!derive) return rows;
  return applyExcelBoqHierarchy(rows);
}

export function parseEst3BoqImportRow(
  row: unknown[],
  cols: ResolvedBoqImportColumns,
  excelRowNumber: number
): Est3BoqImportPreviewRow | null {
  if (!row || !row.some((c) => String(c ?? '').trim())) return null;

  const description = cellStr(row, cols.description).replace(/\s+/g, ' ').trim();
  const itemNumber = cellStr(row, cols.itemNumber) || undefined;
  const parentItemNumber = cellStr(row, cols.parentItemNumber) || undefined;

  const qtyMapped = cols.quantity >= 0;
  const rateMapped = cols.unitRate >= 0;
  const quantityRaw = qtyMapped ? cellNum(row, cols.quantity) : NaN;
  const unitRateRaw = rateMapped ? cellNum(row, cols.unitRate) : NaN;
  const uom = cellStr(row, cols.uom) || undefined;
  const notes = cellStr(row, cols.notes) || undefined;
  const amountText = cols.amount >= 0 ? cellStr(row, cols.amount) : '';
  const amountRaw = amountText ? Number(amountText.replace(/,/g, '')) : NaN;

  const warnings: string[] = [];
  let status: Est3BoqImportPreviewRow['status'] = 'valid';
  let errorMessage: string | undefined;

  if (cols.description < 0) {
    status = 'error';
    errorMessage = 'Description column is not mapped';
  } else if (!description) {
    status = 'error';
    errorMessage = 'Description is required';
  } else if (qtyMapped && cellStr(row, cols.quantity) && (!Number.isFinite(quantityRaw) || quantityRaw < 0)) {
    status = 'error';
    errorMessage = 'Quantity must be a non-negative number';
  } else if (
    rateMapped &&
    cellStr(row, cols.unitRate) &&
    (!Number.isFinite(unitRateRaw) || unitRateRaw < 0)
  ) {
    status = 'error';
    errorMessage = 'Unit Rate must be a non-negative number';
  }

  if (status !== 'error') {
    if (!qtyMapped) warnings.push('Quantity unmapped — defaulting to 0');
    else if (!cellStr(row, cols.quantity)) warnings.push('Quantity blank — defaulting to 0');
    if (!rateMapped) warnings.push('Unit Rate unmapped — defaulting to 0');
    else if (!cellStr(row, cols.unitRate)) warnings.push('Unit Rate blank — defaulting to 0');

    const quantity = Number.isFinite(quantityRaw) ? quantityRaw : 0;
    const unitRate = Number.isFinite(unitRateRaw) ? unitRateRaw : 0;
    if (Number.isFinite(amountRaw) && Math.abs(quantity * unitRate - amountRaw) > AMOUNT_EPSILON) {
      warnings.push('Amount column does not match Quantity × Unit Rate');
    }
    if (warnings.length) status = 'warning';
  }

  return {
    key: excelRowNumber,
    row: excelRowNumber,
    itemNumber,
    description: description || '(missing)',
    parentItemNumber,
    quantity: Number.isFinite(quantityRaw) ? quantityRaw : 0,
    uom,
    unitRate: Number.isFinite(unitRateRaw) ? unitRateRaw : 0,
    notes,
    amountText: amountText || undefined,
    status,
    errorMessage,
    warningMessage: warnings.length ? warnings.join('; ') : undefined,
    detectedPreset: cols.preset,
  };
}

export function parseEst3BoqSheet(
  aoa: unknown[][],
  mapping: BoqImportMappingPayload = {}
): Est3BoqImportPreviewRow[] {
  if (!aoa.length) return [];
  const headerRowIndex = Math.min(mapping.headerRowIndex ?? 0, Math.max(0, aoa.length - 1));
  const headerRow = (aoa[headerRowIndex] ?? []).map((h) => String(h ?? ''));
  const cols = resolveBoqImportColumns(headerRow, {
    ...mapping,
    headerRowIndex,
  });

  if (cols.description < 0) {
    return [
      {
        key: headerRowIndex + 1,
        row: headerRowIndex + 1,
        description: '(missing)',
        quantity: 0,
        unitRate: 0,
        status: 'error',
        errorMessage: `Missing required Description column for ${cols.preset} format`,
        detectedPreset: cols.preset,
      },
    ];
  }

  const parsed: Est3BoqImportPreviewRow[] = [];
  for (let i = headerRowIndex + 1; i < aoa.length; i++) {
    const row = parseEst3BoqImportRow(aoa[i] as unknown[], cols, i + 1);
    if (row) parsed.push(row);
  }
  return applyParentsFromExcelItemNumbers(parsed, cols.deriveParentFromItemNumber !== false);
}

export async function readEst3BoqImportWorkbook(file: File): Promise<Est3BoqImportWorkbookPreview> {
  const XLSX = await import('xlsx');
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: 'array' });
  const sheetNames = workbook.SheetNames || [];
  const sheets = sheetNames.map((name) => {
    const worksheet = workbook.Sheets[name];
    const aoa = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as unknown[][];
    return { name, aoa };
  });
  const first = sheets[0];
  if (!first) {
    return {
      sheetName: '',
      sheetNames: [],
      headerRowIndex: 0,
      headers: [],
      sampleRows: [],
      aoa: [],
      sheets: [],
    };
  }
  const headerRowIndex = 0;
  const headers = (first.aoa[headerRowIndex] ?? []).map((h) => String(h ?? ''));
  const sampleRows = first.aoa.slice(headerRowIndex + 1, headerRowIndex + 4);
  return {
    sheetName: first.name,
    sheetNames,
    headerRowIndex,
    headers,
    sampleRows,
    aoa: first.aoa,
    sheets,
  };
}

export function parseEst3BoqWorkbook(
  sheets: Array<{ name: string; aoa: unknown[][] }>,
  mapping: BoqImportMappingPayload = {}
): Est3BoqImportPreviewRow[] {
  const selected = mapping.sheetNames?.length
    ? sheets.filter((s) => mapping.sheetNames!.includes(s.name))
    : sheets;
  const out: Est3BoqImportPreviewRow[] = [];
  for (const s of selected) {
    if (!s.aoa?.length) continue;
    const parsed = parseEst3BoqSheet(s.aoa, mapping).map((r, idx) => ({
      ...r,
      key: out.length + idx + 1,
      sheet: s.name,
    }));
    out.push(...parsed);
  }
  return out;
}

export async function parseEst3BoqImportFile(
  file: File,
  mapping: BoqImportMappingPayload = {}
): Promise<Est3BoqImportPreviewRow[]> {
  const wb = await readEst3BoqImportWorkbook(file);
  return parseEst3BoqWorkbook(wb.sheets, mapping);
}

export function buildMappingPayload(
  preset: Est3BoqImportPreset,
  cols: ResolvedBoqImportColumns,
  headerRowIndex: number,
  deriveParentFromItemNumber = true,
  sheetNames?: string[]
): BoqImportMappingPayload {
  return {
    preset,
    columns: columnsPayloadFromResolved({
      ...cols,
      deriveParentFromItemNumber,
    }),
    deriveParentFromItemNumber,
    headerRowIndex,
    sheetNames,
  };
}
